<div class="share-link-dialog">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-title-group">
      <div class="icon-bubble">
        <mat-icon>share</mat-icon>
      </div>
      <div>
        <h2>Share Document Access</h2>
        <p class="subtitle">Generate & manage secure presigned links</p>
      </div>
    </div>
    <button mat-icon-button class="close-btn" (click)="close()" title="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Document Card Info -->
  <div class="document-banner">
    <mat-icon class="doc-icon">description</mat-icon>
    <div class="doc-info">
      <span class="file-name" [title]="data?.fileName">{{ data?.fileName || 'Document' }}</span>
      <span *ngIf="data?.category" class="category-badge">{{ data?.category }}</span>
    </div>
  </div>

  <!-- Tab Bar -->
  <div class="tab-nav-bar">
    <button
      type="button"
      class="nav-tab-btn"
      [class.active]="selectedTabIndex === 0"
      (click)="selectedTabIndex = 0"
    >
      <mat-icon>add_link</mat-icon>
      <span>Create Link</span>
    </button>

    <button
      type="button"
      class="nav-tab-btn"
      [class.active]="selectedTabIndex === 1"
      (click)="selectedTabIndex = 1; loadExistingLinks()"
    >
      <mat-icon>link</mat-icon>
      <span>Shared Links</span>
      <span *ngIf="activeLinksCount > 0" class="tab-count-badge">{{ activeLinksCount }}</span>
    </button>
  </div>

  <!-- TAB 0: CREATE SHARE LINK -->
  <div *ngIf="selectedTabIndex === 0" class="dialog-body">
    <!-- Optional Recipient / Note Label -->
    <div class="section-group">
      <label class="section-label">
        <mat-icon class="section-icon">label</mat-icon>
        Recipient / Note (Optional)
      </label>
      <input
        type="text"
        class="text-input"
        placeholder="e.g. Sent to Client, Legal Counsel review, Artist group"
        [(ngModel)]="linkLabel"
      />
    </div>

    <!-- Duration Section -->
    <div class="section-group">
      <label class="section-label">
        <mat-icon class="section-icon">timer</mat-icon>
        Link Lifespan & Expiration
      </label>
      <div class="duration-grid">
        <button
          *ngFor="let opt of durationPresets"
          type="button"
          class="duration-pill"
          [class.active]="selectedDuration === opt.minutes"
          (click)="selectDuration(opt.minutes)"
        >
          <mat-icon class="pill-icon">{{ opt.icon }}</mat-icon>
          <span class="pill-label">{{ opt.label }}</span>
          <span *ngIf="opt.badge" class="pill-badge">{{ opt.badge }}</span>
        </button>
      </div>
    </div>

    <!-- Access Scope Toggle -->
    <div class="section-group">
      <label class="section-label">
        <mat-icon class="section-icon">shield</mat-icon>
        Access Permission
      </label>
      <div class="scope-toggle-group">
        <button
          type="button"
          class="scope-btn"
          [class.active]="accessType === 'view'"
          (click)="setAccessType('view')"
        >
          <mat-icon>visibility</mat-icon>
          <span>Inline Preview Only</span>
        </button>
        <button
          type="button"
          class="scope-btn"
          [class.active]="accessType === 'download'"
          (click)="setAccessType('download')"
        >
          <mat-icon>file_download</mat-icon>
          <span>Direct Attachment Download</span>
        </button>
      </div>
    </div>

    <!-- Error Alert -->
    <div *ngIf="errorMessage" class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- Generate Action Button (Before Link is Generated) -->
    <div *ngIf="!hasGenerated" class="action-submit-row">
      <button
        type="button"
        mat-flat-button
        color="primary"
        class="generate-main-btn"
        [disabled]="isGenerating"
        (click)="generateLink()"
      >
        <mat-icon class="btn-icon" [class.spinning]="isGenerating">
          {{ isGenerating ? 'sync' : 'link' }}
        </mat-icon>
        <span>{{ isGenerating ? 'Generating Secure Link...' : 'Generate Share Link' }}</span>
      </button>
    </div>

    <!-- Share Link Output Box (Only Shown After User Clicks Generate) -->
    <div *ngIf="hasGenerated && generatedLink" class="section-group link-output-section animate-fade-in">
      <div class="output-header-row">
        <label class="section-label success-label">
          <mat-icon class="section-icon success-icon">check_circle</mat-icon>
          Shareable Link Ready!
        </label>
        <button type="button" class="btn-create-another" (click)="resetToCreateAnother()">
          <mat-icon>add</mat-icon>
          <span>Create Another</span>
        </button>
      </div>

      <div class="link-box">
        <input
          type="text"
          class="link-input"
          [value]="generatedLink"
          readonly
          (click)="$any($event.target).select()"
        />

        <button
          mat-stroked-button
          class="copy-btn"
          [class.copied]="isCopied"
          (click)="copyToClipboard()"
        >
          <mat-icon>{{ isCopied ? 'check' : 'content_copy' }}</mat-icon>
          <span>{{ isCopied ? 'Copied!' : 'Copy Link' }}</span>
        </button>

        <button
          mat-icon-button
          class="open-btn"
          (click)="openLink()"
          title="Open in new tab"
        >
          <mat-icon>open_in_new</mat-icon>
        </button>
      </div>

      <!-- Expiration status description -->
      <div class="expiration-status">
        <ng-container *ngIf="isPermanent">
          <span class="status-permanent">
            <mat-icon>all_inclusive</mat-icon>
            Permanent Link — This link will never expire.
          </span>
        </ng-container>
        <ng-container *ngIf="!isPermanent && expiresAt">
          <span class="status-timed">
            <mat-icon>schedule</mat-icon>
            Expires on: <strong>{{ expiresAt | date:'medium' }}</strong>
          </span>
        </ng-container>
      </div>
    </div>
  </div>

  <!-- TAB 1: MANAGE & REVOKE ACTIVE SHARED LINKS -->
  <div *ngIf="selectedTabIndex === 1" class="dialog-body manage-body">
    <!-- Top Action Bar -->
    <div class="manage-header-row">
      <span class="active-count-label">
        <strong>{{ activeLinksCount }}</strong> active link(s) for this file
      </span>

      <button
        *ngIf="activeLinksCount > 0"
        mat-flat-button
        color="warn"
        class="revoke-all-btn"
        [disabled]="isRevokingAll"
        (click)="revokeAllActiveLinks()"
      >
        <mat-icon>block</mat-icon>
        <span>{{ isRevokingAll ? 'Revoking...' : 'Revoke All Active Links' }}</span>
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoadingLinks" class="loading-links-state">
      <mat-icon class="spinning">sync</mat-icon>
      <span>Loading shared links...</span>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoadingLinks && existingLinks.length === 0" class="empty-links-state">
      <mat-icon class="empty-icon">link_off</mat-icon>
      <h4>No Shared Links Yet</h4>
      <p>Switch to "Create Link" tab above to generate a secure presigned link.</p>
    </div>

    <!-- Links List -->
    <div *ngIf="!isLoadingLinks && existingLinks.length > 0" class="links-list-container">
      <div
        *ngFor="let link of existingLinks"
        class="link-card"
        [class.revoked]="link.isRevoked"
        [class.expired]="link.isExpired && !link.isRevoked"
      >
        <!-- Card Left: Status & Meta -->
        <div class="card-main-info">
          <div class="card-title-row">
            <span *ngIf="link.isRevoked" class="status-pill status-revoked">Revoked</span>
            <span *ngIf="!link.isRevoked && link.isExpired" class="status-pill status-expired">Expired</span>
            <span *ngIf="!link.isRevoked && !link.isExpired" class="status-pill status-active">Active</span>

            <span class="scope-pill">{{ link.accessType === 'download' ? 'Download' : 'View' }}</span>

            <span class="link-label-text" [title]="link.label || 'Unnamed link'">
              {{ link.label || 'Direct Share Link' }}
            </span>
          </div>

          <div class="card-meta-row">
            <span>Created: {{ link.dateCreated | date:'short' }}</span>
            <span *ngIf="link.isPermanent" class="perm-text">• ♾️ Permanent</span>
            <span *ngIf="!link.isPermanent && link.expiresAt && !link.isRevoked">
              • Expires: {{ link.expiresAt | date:'short' }}
            </span>
            <span *ngIf="link.isRevoked" class="revoked-date">
              • Revoked on: {{ link.revokedAt | date:'short' }}
            </span>
          </div>
        </div>

        <!-- Card Right: Actions -->
        <div class="card-actions">
          <ng-container *ngIf="!link.isRevoked && !link.isExpired">
            <button
              mat-icon-button
              class="card-action-btn"
              [class.copied]="copiedLinkId === link.id"
              (click)="copyExistingLink(link)"
              [matTooltip]="copiedLinkId === link.id ? 'Copied!' : 'Copy Link'"
            >
              <mat-icon>{{ copiedLinkId === link.id ? 'check' : 'content_copy' }}</mat-icon>
            </button>

            <button
              mat-icon-button
              class="card-action-btn"
              (click)="openExistingLink(link)"
              matTooltip="Open in new tab"
            >
              <mat-icon>open_in_new</mat-icon>
            </button>

            <button
              mat-stroked-button
              color="warn"
              class="revoke-single-btn"
              [disabled]="revokingIds.has(link.id)"
              (click)="revokeSingleLink(link)"
              matTooltip="Revoke external access immediately"
            >
              <mat-icon>do_not_disturb_on</mat-icon>
              <span>{{ revokingIds.has(link.id) ? 'Revoking...' : 'Revoke' }}</span>
            </button>
          </ng-container>

          <span *ngIf="link.isRevoked" class="revoked-badge-text">Access Blocked</span>
          <span *ngIf="!link.isRevoked && link.isExpired" class="expired-badge-text">Link Expired</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Dialog Footer -->
  <div class="dialog-footer">
    <button mat-button class="btn-cancel" (click)="close()">Done</button>
  </div>
</div>
